import Alpine from 'alpinejs'

window.Alpine = Alpine;

document.addEventListener('alpine:init', () => {
    window.scrollCatalogueRow = (id, direction = 1) => {
        const row = document.getElementById(id);

        if (!row) {
            return;
        }

        const amount = Math.max(320, Math.floor(row.clientWidth * 0.9));
        row.scrollBy({ left: direction * amount, behavior: 'smooth' });
    };

    Alpine.store('cartDrawer', {
        open: false,
        desktopPinned: false,
        initialized: false,
        wasDesktop: false,

        init() {
            if (this.initialized) {
                return;
            }

            this.initialized = true;

            const storedState = window.localStorage.getItem('cartDrawer.desktopPinned');

            this.desktopPinned = storedState === null ? false : storedState === 'true';
            this.wasDesktop = this.isDesktop();

            this.syncViewport();

            window.addEventListener('resize', () => this.onResize());
            window.addEventListener('open-cart-drawer', () => this.openDrawer());
            window.addEventListener('toggle-cart-drawer', () => this.toggleDrawer());
            window.addEventListener('close-cart-drawer', () => this.closeDrawer());
        },

        isDesktop() {
            return window.innerWidth >= 1024;
        },

        // Toggling the page scrollbar (updateBodyLock(), triggered whenever
        // the drawer opens/closes on mobile) changes window.innerWidth by
        // the scrollbar's own width, which fires a native resize event even
        // though nothing the user did should affect the drawer. Reacting to
        // every resize by unconditionally recomputing `open` meant that near
        // the 1024px breakpoint, opening the drawer would immediately
        // trigger a resize that closed it again. Only re-sync when the
        // desktop/mobile breakpoint is actually crossed.
        onResize() {
            const isDesktop = this.isDesktop();

            if (isDesktop === this.wasDesktop) {
                return;
            }

            this.wasDesktop = isDesktop;

            this.syncViewport();
        },

        syncViewport() {
            this.open = this.isDesktop() ? this.desktopPinned : false;

            this.updateBodyLock();
        },

        persist() {
            window.localStorage.setItem('cartDrawer.desktopPinned', this.desktopPinned ? 'true' : 'false');
        },

        updateBodyLock() {
            const shouldLock = !this.isDesktop() && this.open;

            document.documentElement.classList.toggle('overflow-hidden', shouldLock);
            document.body.classList.toggle('overflow-hidden', shouldLock);
        },

        openDrawer() {
            this.open = true;

            if (this.isDesktop()) {
                this.desktopPinned = true;
                this.persist();
            }

            this.updateBodyLock();
        },

        closeDrawer() {
            this.open = false;

            if (this.isDesktop()) {
                this.desktopPinned = false;
                this.persist();
            }

            this.updateBodyLock();
        },

        toggleDrawer() {
            if (this.isDesktop()) {
                this.desktopPinned = !this.desktopPinned;
                this.open = this.desktopPinned;
                this.persist();
                this.updateBodyLock();

                return;
            }

            this.open = !this.open;
            this.updateBodyLock();
        },
    });

    Alpine.data('siteShell', () => ({
        init() {
            Alpine.store('cartDrawer').init();
        },
    }));

    Alpine.data('cataloguePage', ({ canPersist = false } = {}) => ({
        storageKey: 'catalogue.last-state',
        saveTimer: null,
        observer: null,
        transitioning: false,
        transitionTimer: null,
        loadMorePending: false,
        pendingScrollToProductsTop: false,

        init() {
            const savedState = this.getSavedState();
            const currentUrl = this.normalizeUrl(window.location.href);

            if (savedState.url === currentUrl) {
                requestAnimationFrame(() => {
                    this.restoreScroll(savedState.scrollTop ?? 0);
                });
            }

            if (new URL(window.location.href).searchParams.get('open_cart') === '1') {
                window.dispatchEvent(new CustomEvent('open-cart-drawer'));
                window.history.replaceState({}, '', currentUrl);
            }

            window.addEventListener('scroll', () => this.saveState(), { passive: true });
            window.addEventListener('scroll', () => this.checkLoadMoreVisibility(), { passive: true });
            window.addEventListener('resize', () => this.checkLoadMoreVisibility(), { passive: true });
            window.addEventListener('beforeunload', () => this.saveState());
            window.addEventListener('catalogue-resume-cleared', () => this.clearSavedState());

            this.bindInfiniteScroll();

            if (window.Livewire) {
                window.Livewire.hook('message.sent', (message, component) => {
                    if (component.fingerprint?.name !== 'catalogue') {
                        return;
                    }

                    if (this.shouldScrollToProductsTop(message)) {
                        this.pendingScrollToProductsTop = true;
                    }

                    if (!this.shouldAnimateMessage(message)) {
                        return;
                    }

                    this.startTransition();
                });

                window.Livewire.hook('message.processed', () => {
                    this.loadMorePending = false;
                    this.bindInfiniteScroll();
                    this.checkLoadMoreVisibility();

                    if (this.pendingScrollToProductsTop) {
                        this.pendingScrollToProductsTop = false;
                        this.scrollToProductsTop();
                    }
                });

                window.Livewire.hook('message.processed', (message, component) => {
                    if (component.fingerprint?.name !== 'catalogue') {
                        return;
                    }

                    if (!this.shouldAnimateMessage(message)) {
                        return;
                    }

                    this.finishTransition();
                });
            }
        },

        shouldAnimateMessage(message) {
            const updates = Array.isArray(message?.updateQueue) ? message.updateQueue : [];

            return updates.some((update) => {
                if (update.type === 'callMethod') {
                    return ['loadMore', 'setCategoryFilter', 'setBrandFilter', 'setCategoryAndBrandFilter', 'resetFilters'].includes(update.payload?.method);
                }

                if (update.type === 'syncInput') {
                    return ['search', 'brand', 'category', 'type', 'resultsSort'].includes(update.payload?.name);
                }

                return false;
            });
        },

        shouldScrollToProductsTop(message) {
            const updates = Array.isArray(message?.updateQueue) ? message.updateQueue : [];

            return updates.some((update) => {
                if (update.type === 'callMethod') {
                    return ['setCategoryFilter', 'setBrandFilter', 'setCategoryAndBrandFilter'].includes(update.payload?.method);
                }

                if (update.type === 'syncInput') {
                    return update.payload?.name === 'category';
                }

                return false;
            });
        },

        startTransition() {
            clearTimeout(this.transitionTimer);
            this.transitioning = true;
        },

        finishTransition() {
            clearTimeout(this.transitionTimer);
            this.transitionTimer = setTimeout(() => {
                this.transitioning = false;
            }, 140);
        },

        visitProduct(url) {
            this.saveState();
            Alpine.store('cartDrawer').closeDrawer();

            const target = new URL(url, window.location.origin);

            target.searchParams.set('return_to', this.normalizeUrl(window.location.href));

            window.location.assign(target.toString());
        },

        normalizeUrl(url) {
            const parsed = new URL(url, window.location.origin);

            parsed.searchParams.delete('open_cart');

            const query = parsed.searchParams.toString();

            return `${parsed.pathname}${query ? `?${query}` : ''}`;
        },

        currentScrollPosition() {
            return window.scrollY || window.pageYOffset || 0;
        },

        restoreScroll(scrollTop) {
            window.scrollTo(0, scrollTop);
        },

        scrollToProductsTop() {
            if (!this.$refs.productsTop) {
                return;
            }

            const headerOffset = this.$refs.filterBar
                ? this.$refs.filterBar.getBoundingClientRect().top + this.$refs.filterBar.getBoundingClientRect().height + 24
                : 24;

            const top = this.$refs.productsTop.getBoundingClientRect().top + window.scrollY - headerOffset;

            window.scrollTo({
                top: Math.max(0, top),
                behavior: 'smooth',
            });
        },

        saveState() {
            const payload = {
                url: this.normalizeUrl(window.location.href),
                scrollTop: this.currentScrollPosition(),
            };

            window.sessionStorage.setItem(this.storageKey, JSON.stringify(payload));

            if (!canPersist || !this.$wire) {
                return;
            }

            clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => {
                this.$wire.saveResumeState(payload);
            }, 250);
        },

        getSavedState() {
            try {
                return JSON.parse(window.sessionStorage.getItem(this.storageKey) || '{}');
            } catch (error) {
                return {};
            }
        },

        clearSavedState() {
            window.sessionStorage.removeItem(this.storageKey);
        },

        bindInfiniteScroll() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }

            if (!this.$refs.loadMoreSentinel || !this.$wire) {
                return;
            }

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.requestLoadMore();
                    }
                });
            }, {
                root: null,
                rootMargin: '200px 0px',
                threshold: 0,
            });

            this.observer.observe(this.$refs.loadMoreSentinel);
        },

        requestLoadMore() {
            if (this.loadMorePending || !this.$wire || !this.$refs.loadMoreSentinel) {
                return;
            }

            this.loadMorePending = true;
            this.$wire.loadMore();
        },

        checkLoadMoreVisibility() {
            if (this.loadMorePending || !this.$wire || !this.$refs.loadMoreSentinel) {
                return;
            }

            const rect = this.$refs.loadMoreSentinel.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

            if (rect.top <= viewportHeight + 220) {
                this.requestLoadMore();
            }
        },
    }));
});

Alpine.start();
